// src/templateManager/components/tabs/RootCausesTab.jsx
import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Stack,
  Card,
  Text,
  Group,
  Alert,
  TagsInput,
  Textarea,
  Badge
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

/**
 * RootCausesTab - Configure root cause categories
 * These categories apply to all risk areas
 */
function RootCausesTab() {
  const { control, watch } = useFormContext();

  const rootCauses = watch('taxonomy.rootCauses') || {};

  // Root cause categories
  const categories = [
    {
      key: 'accuracy',
      label: 'Accuracy',
      description: 'Model accuracy parameters and related metrics',
      icon: '🎯'
    },
    {
      key: 'fairnessAndBias',
      label: 'Fairness & Bias',
      description: 'Factors related to algorithmic fairness and bias',
      icon: '⚖️'
    },
    {
      key: 'performanceAndRobustness',
      label: 'Performance & Robustness',
      description: 'Performance and robustness factors',
      icon: '⚡'
    },
    {
      key: 'explainabilityAndTransparency',
      label: 'Explainability & Transparency',
      description: 'Aspects of model explainability and transparency',
      icon: '💡'
    }
  ];

  return (
    <Stack gap="lg">
      {/* Info */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue">
        Root causes are potential underlying reasons for risks. These categories apply to all risk areas
        and help users identify the source of problems during risk assessment.
      </Alert>

      {/* Categories */}
      <Stack gap="md">
        {categories.map((category) => {
          const optionsCount = rootCauses[category.key]?.options?.length || 0;

          return (
            <Card key={category.key} withBorder p="lg">
              <Stack gap="md">
                <Group>
                  <Text size="xl">{category.icon}</Text>
                  <div style={{ flex: 1 }}>
                    <Group gap="xs">
                      <Text fw={600} size="lg">{category.label}</Text>
                      <Badge size="sm" variant="light">
                        {optionsCount} options
                      </Badge>
                    </Group>
                    <Text size="sm" c="dimmed">{category.description}</Text>
                  </div>
                </Group>

                {/* Options */}
                <Controller
                  name={`taxonomy.rootCauses.${category.key}.options`}
                  control={control}
                  render={({ field }) => (
                    <TagsInput
                      {...field}
                      label="Root Cause Options"
                      placeholder="Type option and press Enter"
                      description="Enter potential root causes for this category"
                      splitChars={[',']}
                    />
                  )}
                />

                {/* Description (optional override) */}
                <Controller
                  name={`taxonomy.rootCauses.${category.key}.description`}
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      label="Custom Description (optional)"
                      placeholder={category.description}
                      minRows={2}
                    />
                  )}
                />
              </Stack>
            </Card>
          );
        })}
      </Stack>

      {/* Summary */}
      <Card withBorder p="md" bg="gray.0">
        <Stack gap="xs">
          <Text size="sm" fw={600}>Summary</Text>
          <Group gap="md">
            <Badge variant="dot">
              Total Categories: {categories.length}
            </Badge>
            <Badge variant="dot" color="blue">
              Total Options: {categories.reduce((sum, cat) => sum + (rootCauses[cat.key]?.options?.length || 0), 0)}
            </Badge>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

export default RootCausesTab;
